import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight } from 'lucide-react';
import { createHorizontalScroll, fadeInOnScroll } from '../utils/animations';
import { projects } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const Work = () => {
  const horizontalRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    // Animate header
    if (headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, stagger: 0.2, duration: 1, ease: 'power3.out' }
      );
    }

    // Horizontal scroll
    if (horizontalRef.current) {
      const container = horizontalRef.current;
      const items = container.querySelectorAll('.horizontal-item');
      
      if (items.length > 0) {
        createHorizontalScroll(container, items);
      }
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div className="work-page pt-32">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <div ref={headerRef} className="text-center">
          <span className="inline-block px-3 py-1 bg-[var(--surface-container-high)] text-[var(--on-surface-variant)] font-body text-xs tracking-code uppercase mb-6 rounded-sm">
            Our Portfolio
          </span>
          <h1 className="font-headline text-5xl md:text-7xl font-bold tracking-headline uppercase mb-8">
            Selected <span className="text-[var(--primary)]">Works</span>
          </h1>
          <p className="font-body text-[var(--on-surface-variant)] text-xl max-w-3xl mx-auto leading-relaxed">
            A curated collection of our most ambitious projects,
            <br />
            where technical excellence meets creative innovation
          </p>
        </div>
      </section>

      {/* Horizontal Scroll Gallery */}
      <section ref={horizontalRef} className="horizontal-scroll-section h-screen relative overflow-hidden">
        <div className="flex gap-8 px-6 lg:px-8 items-center h-full">
          {projects.map((project, index) => (
            <Link
              key={project.id}
              to={`/work/${project.id}`}
              className="horizontal-item flex-shrink-0 w-[80vw] md:w-[60vw] lg:w-[45vw] group"
            >
              <div className="relative overflow-hidden bg-[var(--surface-container)] h-[70vh]">
                {/* Image */}
                <div className="absolute inset-0">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface-container-lowest)] via-transparent to-transparent opacity-90" />
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
                  {/* Category */}
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-[var(--tertiary)] font-body text-xs tracking-code uppercase">
                      {project.category}
                    </span>
                    <div className="h-px flex-1 bg-[var(--outline-variant)]/20" />
                    <span className="text-[var(--on-surface-variant)] font-body text-xs tracking-code uppercase">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-headline text-3xl lg:text-4xl font-bold tracking-headline uppercase mb-4">
                    {project.title}
                  </h3>

                  {/* Description */}
                  <p className="font-body text-[var(--on-surface-variant)] mb-6 max-w-lg">
                    {project.description}
                  </p>

                  {/* Tags */}
                  {project.tags && (
                    <div className="flex gap-2 mb-6">
                      {project.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-[var(--secondary-container)]/40 text-[var(--secondary)] text-xs font-bold tracking-code uppercase rounded-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* CTA */}
                  <div className="flex items-center gap-2 text-[var(--primary)] font-headline text-sm font-bold uppercase tracking-code group-hover:gap-3 transition-all">
                    View Case Study
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>

                {/* Top border on hover */}
                <div className="absolute top-0 left-0 w-full h-1 bg-[var(--primary)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </div>
            </Link>
          ))}
        </div>

        {/* Scroll Hint */}
        <div className="absolute bottom-8 right-8 text-[var(--on-surface-variant)] font-body text-sm tracking-code uppercase flex items-center gap-2">
          <span>Scroll horizontally</span>
          <ArrowRight size={16} />
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-32 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center">
          <h2 className="font-headline text-4xl md:text-6xl font-bold tracking-headline uppercase mb-8">
            Ready to create <br />
            <span className="text-[var(--primary)]">Something Amazing?</span>
          </h2>
          <p className="font-body text-[var(--on-surface-variant)] text-lg mb-12 max-w-2xl mx-auto">
            Let's discuss how we can bring your vision to life with cutting-edge technology
          </p>
          <Link
            to="/contact"
            className="magnetic inline-block bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-10 py-4 font-headline font-bold text-sm uppercase tracking-code hover:brightness-110 hover:scale-105 transition-all duration-300 rounded-none"
          >
            Start a Conversation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Work;
